import {
  Button,
  ButtonsWrapper,
  Form,
  Modal,
  NOTIFICATION_TYPE,
  TextInput,
  showNotification,
} from '@thinknet-gmbh/platform-common-ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetchKwpData } from 'src/hooks/interfaces/kwp/fetch-data.hook';
import { useCurrentMemberContext } from 'src/contexts';
import { useCreateManyProjects } from 'src/hooks/projects/create-many-projects.hook';
import { useCreateManyCustomers } from 'src/hooks/customers/create-many-customers.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { CUSTOMERS_MODALS } from 'src/pages/customers/customers.constants';
import { FormContainer } from './kwp-fetching-data-modal-component.styles';
import { transformCustomersForSaving, transformProjectsForSaving } from './helpers';
import { INTERFACES_API_URL } from '../../../interfaces.constants';

export const KwpFetchingDataModal = ({
  onClose,
  userLink,
  activeModal,
}: {
  onClose: () => void;
  userLink: string;
  activeModal: string;
}) => {
  const { t } = useTranslation(['common', 'settings', 'customers', 'projects']);
  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState<string>();

  const isCustomersModal = activeModal === CUSTOMERS_MODALS.kwpCustomers;

  const { fetchData, data } = useFetchKwpData(
    userLink,
    isCustomersModal ? INTERFACES_API_URL.CUSTOMERS : INTERFACES_API_URL.PROJECTS
  );

  useEffect(() => {
    if (formValues) {
      setTimeout(() => {
        fetchData(
          formValues.includes(',') ? Array.from(formValues.split(', ')) : [formValues],
          isCustomersModal
        );
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues]);

  const currentCompanyId = useGetCurrentCompanyId();
  const { ganttId, currentMemberId } = useCurrentMemberContext();

  const { createCustomers } = useCreateManyCustomers({ onSuccess: onClose });
  const { createProjects } = useCreateManyProjects({ onSuccess: onClose });

  const mutationHandler = isCustomersModal ? createCustomers : createProjects;

  const onFinish = async (values: any) => {
    if (data.length > 0) {
      const dataForSaving = isCustomersModal
        ? transformCustomersForSaving(data, currentCompanyId)
        : transformProjectsForSaving(data, ganttId, currentMemberId, currentCompanyId, userLink);

      await mutationHandler({
        variables: {
          payload: dataForSaving,
        },
      });
    } else if (!data.length && formValues) {
      showNotification({
        type: NOTIFICATION_TYPE.error,
        message: isCustomersModal
          ? t('customers:notifications.customerNotFound')
          : t('projects:notifications.projectsNotFound'),
      });
    }
  };

  return (
    <Modal isOpened title={t('common:labels.getFromKwp')} onClose={onClose} modalWidth={336}>
      <Form layout="vertical" form={form} onFinish={onFinish}>
        <FormContainer>
          <div>
            <Form.Item
              name="inputData"
              label={
                isCustomersModal
                  ? t('customers:labels.addressCodes')
                  : t('projects:labels.projectsNumbers')
              }
              rules={[{ required: true, message: t('customers:error.fieldIsRequired') }]}
            >
              <TextInput
                placeholder={isCustomersModal ? 'Code1, CODE2' : 'P230005, P231004'}
                maxLength={50}
                onChange={e => {
                  setFormValues(e.target.value);
                }}
              />
            </Form.Item>
          </div>
        </FormContainer>
        <ButtonsWrapper>
          <Button htmlType="submit" type="primary">
            {t('common:buttons.synchronize')}
          </Button>
          <Button onClick={onClose}>{t('common:buttons.cancel')}</Button>
        </ButtonsWrapper>
      </Form>
    </Modal>
  );
};
